import * as React from 'react';
import * as _ from 'underscore';

import { ButtonWidget, ButtonType, TabStyles } from '@tableau/widgets';

import { ISpaceItemDetailInfo } from '../../../ts/models/ISpaceItemDetailInfo';
import { SiteUrlsProps } from '../../selectors/SiteUrlsSelectors';
import { ConnectedSpaceHeader } from '../Header/SpaceHeader';
import { FavoriteToggleWithCountsIcon, ActionsMenuIcon } from '../Header/HeaderNameRowIcons';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { CollectionShareTestId, CollectionPrivateHeader } from './CollectionSpace';
import { Messages } from '../../../codegen/Localize';
import { imgPath } from '../../../common/utilities/ImgUrl';
import { cssClass } from '../../../style';
import { OwnerMetadata, ModifiedDateMetadata, VerticalDivider, DefaultVerticalDividerPadding } from '../Header/HeaderMetadataRow';
import { RadioEditProvider } from '../../../dataRole/utils/RadioEditProvider';
import { EditableCollectionNameComponent } from './CollectionHeaderEditableNameComponent';
import { EditableCollectionDescriptionInline } from './CollectionHeaderEditableDescription';

const LOCKED_IMG = imgPath('lock-locked-small.svg');

const NameRowStyle = cssClass('collection-header', 'name-row', {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
  flexGrow: 1,
  $nest: {
    '&>*': {
      marginRight: TabStyles.Spacing.MediumSpace
    },
    '&>*:last-child': {
      marginRight: 0
    }
  }
});

export interface CollectionSpaceHeaderProps extends SiteUrlsProps {
  readonly item: ISpaceItemDetailInfo<'collection'>;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
  readonly onShare: () => void;
  readonly isPublicCollectionsEnabled: boolean;
  readonly selectedContentItemsCount: number;
  readonly isAdminOrOwner: boolean;
  readonly sanitizer: ng.sanitize.ISanitizeService;
  readonly openDetail: () => void;
}

export function CollectionSpaceHeader(props: CollectionSpaceHeaderProps): JSX.Element {
  const actionsMenuDisabled = props.selectedContentItemsCount > 0;
  const actionsMenuButtonTitle = actionsMenuDisabled ? Messages.collectionActionsMenuDisabled() : null;
  const canEdit = props.isAdminOrOwner;

  return <RadioEditProvider isReadonly={!canEdit}>
    <ConnectedSpaceHeader
      item={props.item}
      availableActions={undefined}
      nameElement={<EditableCollectionNameComponent {...props}/>}
      hideHeaderIcon={true}
      nameRowIcons={
        <div className={NameRowStyle}>
          {FeatureFlags.favoriteCollectionsEnabled && FavoriteToggleWithCountsIcon({
            item: props.item,
            addToFavorites: props.addToFavorites,
            removeFromFavorites: props.removeFromFavorites,
          })}
          {ActionsMenuIcon(props.item, actionsMenuDisabled, actionsMenuButtonTitle)}
          {FeatureFlags.newShareDialogEnabled && FeatureFlags.shareCollectionsEnabled &&
            <ButtonWidget
              style={{ marginLeft: 'auto' }}
              buttonType={ButtonType.Outline}
              handleClick={props.onShare}
              testId={CollectionShareTestId}
              disabled={!props.isPublicCollectionsEnabled}
              title={!props.isPublicCollectionsEnabled ?
                Messages.collectionsDisabledShareSettingTooltip() : ''}
            >
              {Messages.shareButtonCollection()}
            </ButtonWidget>
          }
        </div>
      }
      descriptionElement={<EditableCollectionDescriptionInline {...props}/>}
      metadataRow={
        <>
          {!_.isUndefined(props.item.owner) && OwnerMetadata(props.item.owner, props.urls)}
          {!_.isUndefined(props.item.owner) && VerticalDivider()}
          {ModifiedDateMetadata(props.item.updatedAt, false)}
          {props.item.visibility === 'private' &&
            <>
              {VerticalDivider(DefaultVerticalDividerPadding, 2)}
              <div data-tb-test-id={CollectionPrivateHeader}>
                <img src={LOCKED_IMG}
                  alt={Messages.collectionsPrivateAlt()}
                />
                <span style={{ marginLeft: 2 }}>{Messages.collectionVisibilityPrivate()}</span>
              </div>
            </>
          }
        </>
      }
    />
  </RadioEditProvider>;
}
